import { register } from 'register-service-worker'
import { Notify } from 'quasar'
import { i18n } from 'boot/i18n'

// Global service worker registration reference
let swRegistration: ServiceWorkerRegistration | null = null

// Listen for messages from the service worker
navigator.serviceWorker?.addEventListener(
  'message',
  (event: { data: { type: string; payload: Record<string, string> } }) => {
    if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
      console.log('Update available:', event.data.payload)

      // Show notification to user about available update
      Notify.create({
        color: 'white',
        textColor: 'dark',
        iconSize: 'lg',
        icon: 'fa-regular fa-cloud-arrow-down',
        badgeStyle: 'display: none',
        message: i18n.global.t('app.pwa.updateAvailable.message'),
        caption: i18n.global.t('app.pwa.updateAvailable.caption'),
        timeout: 0,
        actions: [
          {
            label: i18n.global.t('app.pwa.updateAvailable.download'),
            color: 'dark',
            unelevated: true,
            class: 'border-radius-3 q-px-lg',
            noCaps: true,
            handler: () => {
              // Force service worker update check to get new registration
              void (async () => {
                if (swRegistration) {
                  console.debug('Forcing update check - swRegistration found')
                  try {
                    await swRegistration.update()
                    // If there's a waiting service worker after update, activate it
                    if (swRegistration.waiting) {
                      console.debug('Activating waiting service worker - skipWaiting()')
                      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
                    } else {
                      console.debug('No waiting service worker found - reloading page')
                      // Fallback to page reload if no waiting service worker
                      window.location.reload(true)
                    }
                  } catch (error) {
                    console.error('Failed to update service worker:', error)
                    // Fallback to page reload on error
                    window.location.reload(true)
                  }
                } else {
                  // Fallback to page reload if no registration
                  window.location.reload(true)
                }
              })()
            },
          },
          {
            label: i18n.global.t('app.pwa.updateAvailable.later'),
            color: 'grey-7',
            flat: true,
            noCaps: true,
            handler: () => {
              // Dismiss notification
            },
          },
        ],
      })
    }
  },
)

// Handle service worker updates
navigator.serviceWorker?.addEventListener('controllerchange', () => {
  // Service worker has been updated, reload the page
  window.location.reload()
})

// Export function to force update check
export function forceUpdateCheck() {
  if (swRegistration?.active) {
    swRegistration.active.postMessage({ type: 'FORCE_UPDATE_CHECK' })
  }
}

// Export function to handle visibility changes
export function handleVisibilityChange() {
  if (swRegistration?.active) {
    swRegistration.active.postMessage({ type: 'VISIBILITY_CHANGED' })
  }
}

// The ready(), registered(), cached(), updatefound() and updated()
// events passes a ServiceWorkerRegistration instance in their arguments.
// ServiceWorkerRegistration: https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerRegistration

register(process.env.SERVICE_WORKER_FILE, {
  // The registrationOptions object will be passed as the second argument
  // to ServiceWorkerContainer.register()
  // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorkerContainer/register#Parameter

  // registrationOptions: { scope: './' },

  ready(registration) {
    console.log('Service worker is active.')
    swRegistration = registration
    // Signal to service worker that client is ready
    if (registration.active) {
      registration.active.postMessage({ type: 'CLIENT_READY' })
    }
  },

  registered(registration) {
    console.log('Service worker has been registered.')
    swRegistration = registration
  },

  cached(registration) {
    console.log('Content has been cached for offline use.')
    swRegistration = registration
  },

  updatefound(registration) {
    console.log('New content is downloading.')
    swRegistration = registration
  },

  updated(registration) {
    console.log('New content is available; please refresh.')
    swRegistration = registration

    // Automatically activate the waiting service worker
    // The controllerchange listener will reload the page
    if (registration.waiting) {
      console.debug('Auto-activating waiting service worker - skipWaiting()')
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  },

  offline() {
    console.debug('No internet connection found. App is running in offline mode.')

    // Notify.create({
    //   type: 'warning',
    //   message: i18n.global.t('app.pwa.offline.message'),
    //   caption: i18n.global.t('app.pwa.offline.caption'),
    //   timeout: 3000,
    // })
  },

  error(err) {
    console.error('Error during service worker registration:', err)

    // Notify.create({
    //   type: 'negative',
    //   message: i18n.global.t('app.pwa.error.message'),
    //   caption: i18n.global.t('app.pwa.error.caption'),
    //   timeout: 5000,
    // })
  },
})
